/* eslint-disable react/display-name */
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Create as PreparedIcon,
  RemoveCircle as RejectedIcon,
  CheckCircle as ApprovedIcon,
  Replay as ResetIcon,
} from '@mui/icons-material';
import {
  Activity,
  ActivityType,
  Comment, ListFormCommentsQuery, PreparedForm, Role,
} from 'API';
import CommentService from 'services/comment';
import PersonService from 'services/person';
import PreparedFormService from 'services/preparedForm';
import UserContext from 'shared/contexts/UserContext';
import CaseContext from 'shared/contexts/CaseContext';
import { notNullFilter, shortOrLongDate } from 'shared/utilities';
import { GraphQLResult } from '@aws-amplify/api';
import { onCreateActivityByIndirectTargetID, onCreateComment } from 'graphql/subscriptions';
import CommentBubble from 'components/CommentBubble';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import CommentInput from 'components/CommentInput';
import Tabs from 'components/Tabs';
import { MH_ROLES } from 'services/auth';
import AlertContext from 'shared/contexts/AlertContext';
import { client } from 'lib/graphql-client';

const { fullname } = PersonService;

const canSeeActivityFilter = (person: Person, role: Role) => (activity: Activity) => (
  MH_ROLES.includes(role) || activity.actorID === person.id
);

const canSeeCommentFilter = (person: Person, role: Role) => (comment: Comment) => (
  MH_ROLES.includes(role) || comment.authorID === person.id
);

const getPreparedFormSidebarStyles = (theme: Theme) => ({
  comment: {
    width: '100%',
  },
  iconApproved: {
    color: theme.palette.primary.main,
  },
  iconPrepared: {
    color: theme.palette.text.primary,
  },
  iconRejected: {
    color: theme.palette.error.main,
  },
  root: {
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
      width: 0,
      background: 'transparent',
    },
  },
  subhead: {
    fontSize: '1rem',
    fontWeight: theme.typography.fontWeightBlack,
  },
  tab: {
    padding: '1rem',
  },
});

interface Props {
  className?: string;
  preparedForm: PreparedForm;
  preparedFormName: string;
}

interface ActivityProps {
  activity: Activity;
}

const ActivityItem: React.FC<ActivityProps> = ({ activity }: ActivityProps) => {
  const theme = useTheme();
  const styles = getPreparedFormSidebarStyles(theme);
  let Icon;
  let text;

  const actor = activity.actorEmployer
    || activity.actorForeignNational
    || activity.actorTeamMember;

  if (!actor) {
    return null;
  }

  switch (activity.activityType) {
    case ActivityType.CASE_FORM_PREPARED:
      Icon = PreparedIcon;
      text = 'prepared this form';
      break;

    case ActivityType.CASE_FORM_REJECTED:
      Icon = RejectedIcon;
      text = 'rejected this form';
      break;

    case ActivityType.CASE_FORM_APPROVED:
      Icon = ApprovedIcon;
      text = 'reviewed and approved';
      break;

    case ActivityType.CASE_FORM_RESET:
      Icon = ResetIcon;
      text = 'reset this form';
      break;

    case ActivityType.CASE_FORM_COMPLETED:
      Icon = ApprovedIcon;
      text = 'gave final case manager approval';
      break;

    default:
      return null;
  }

  return (
    <IconRow size="small">
      <IconRowCell type="icon">
        <Icon
          fontSize="small"
          sx={styles.iconPrepared}
        />
      </IconRowCell>
      <IconRowCell type="text">
        <strong>
          {shortOrLongDate(activity.createdAt!)}
          :
        </strong>
        {' '}
        {fullname(actor)}
        {' '}
        {text}
      </IconRowCell>
    </IconRow>
  );
};

interface DetailsTabProps {
  preparedForm: PreparedForm;
  preparedFormName: string;
}

const DetailsTab: React.FC<DetailsTabProps> = ({
  preparedForm,
  preparedFormName,
}: DetailsTabProps) => {
  const theme = useTheme();
  const styles = getPreparedFormSidebarStyles(theme);
  const [userState] = useContext(UserContext);
  const { person } = userState;
  const [thisCase] = useContext(CaseContext);
  const [, setAlert] = useContext(AlertContext);

  const [comment, setComment] = useState<string>('');
  const [action, setAction] = useState<'approve' | 'reject' | undefined>();

  const isAssignedToSelf = preparedForm.assignedToID === person.id;
  const isCaseManager = thisCase?.caseManagerID === person.id;

  const handleFinalApproval = async () => {
    await PreparedFormService.approve(
      preparedForm.id!,
      'final',
      thisCase!,
      preparedFormName,
      comment,
    );
  };

  const handleSubmit = async () => {
    if (!action) {
      return;
    }

    if (action === 'reject') {
      try {
        await PreparedFormService.reject(
          preparedForm.id!, comment, thisCase!, preparedFormName,
        );
        setComment('');
      } catch (err) {
        setAlert(['Something went wrong rejecting this form.', 'error']);
      }
    } else {
      try {
        let approverType: PreparedFormApproverType;

        if (preparedForm.assignedToTeamMember) {
          approverType = 'attorney';
        } else if (preparedForm.assignedToEmployer) {
          approverType = 'sponsor';
        } else if (preparedForm.assignedToNational) {
          approverType = 'national';
        } else {
          throw new Error();
        }

        await PreparedFormService.approve(
          preparedForm.id!,
          approverType,
          thisCase!,
          preparedFormName,
          comment,
        );
        setComment('');
      } catch {
        setAlert(['Something went wrong updating this form.', 'error']);
      }
    }
  };

  if (!preparedForm) {
    return null;
  }

  return (
    <Box sx={styles.tab}>
      <Box>
        {isAssignedToSelf && (
          <>
            <Typography
              sx={styles.subhead}
              component="h2"
            >
              Current actions
            </Typography>

            <RadioGroup
              name="prepared-form-actions"
              value={action}
              onChange={(e) => setAction(
                e.target.value as 'reject' | 'approve',
              )}
            >
              <FormControlLabel
                value="approve"
                control={<Radio color="primary" />}
                label="Approve"
              />
              <FormControlLabel
                value="reject"
                control={<Radio color="primary" />}
                label="Reject"
              />
            </RadioGroup>

            {action === 'reject' && (
              <Box sx={{ mb: 2 }}>
                <CommentInput
                  comment={comment}
                  setComment={setComment}
                  isSubmitting={false}
                  handleComment={() => { }}
                  noSubmit
                />
              </Box>
            )}

            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!action || (action === 'reject' && !comment)}
            >
              Submit
            </Button>

          </>
        )}

        {isCaseManager
          && !isAssignedToSelf
          && !preparedForm.finalCaseManagerApproverID && (
          <Button
            onClick={handleFinalApproval}
            color="primary"
            variant="contained"
          >
            Give Final Approval
          </Button>
        )}
      </Box>
    </Box>
  );
};

interface ActivitiesTabProps {
  preparedForm: PreparedForm;
}

const ActivitiesTab: React.FC<ActivitiesTabProps> = (
  { preparedForm }: ActivitiesTabProps,
) => {
  const theme = useTheme();
  const styles = getPreparedFormSidebarStyles(theme);
  const [comment, setComment] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [
    comments, setComments,
  ] = useState<Array<Comment>>([]);
  const [
    activities, setActivities,
  ] = useState<Array<Activity>>(
    (preparedForm?.activities?.items?.filter(
      notNullFilter,
    ) || []) as Array<Activity>,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [, setAlert] = useContext(AlertContext);
  const [userState] = useContext(UserContext);
  const { person, role } = userState;

  const handleComment = async () => {
    try {
      setIsSubmitting(true);
      await CommentService.create(person.id, comment!, preparedForm.id!);
      setComment('');
    } catch {
      setAlert(['Something went wrong leaving this comment.', 'error']);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const activitySubscription = (client.graphql({
      query: onCreateActivityByIndirectTargetID,
      variables: {
        indirectTargetID: preparedForm.id,
      },
    }) as any).subscribe(
      {
        next: (data: any) => {
          const newActivity = data?.data?.onCreateActivityByIndirectTargetId;

          if (newActivity) {
            setActivities([
              ...activities.filter((activity) => activity.id !== newActivity.id),
              newActivity,
            ]);
          }
        },
        // eslint-disable-next-line no-console
        error: (error: any) => console.warn(error),
      },
    );

    return () => {
      activitySubscription.unsubscribe();
    };
  }, [activities]);

  useEffect(() => {
    const subscription = (client.graphql({
      query: onCreateComment,
      variables: {
        sourceID: preparedForm.id!,
      },
    }) as any).subscribe(
      {
        next: (data: any) => {
          const newComment = data?.data?.onCreateComment;

          if (newComment) {
            setComments([...comments, newComment]);
          }
        },
        // eslint-disable-next-line no-console
        error: (error: any) => console.warn(error),
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [comments]);

  useEffect(() => {
    let ignore = false;

    const loadComments = async () => {
      setIsLoading(true);
      const response = await PreparedFormService.listComments(
        preparedForm.id!,
      ) as GraphQLResult<ListFormCommentsQuery>;

      if (response.data?.getPreparedForm?.comments?.items && !ignore) {
        setComments(response.data.getPreparedForm.comments.items
          .filter(notNullFilter) as Array<Comment>);
      }

      if (!ignore) {
        setIsLoading(false);
      }
    };

    loadComments();

    return () => { ignore = true; };
  }, []);

  return (
    <Box sx={styles.tab}>
      {activities.filter(canSeeActivityFilter(person, role)).length > 0 && (
        <>
          <Typography
            sx={styles.subhead}
            component="h2"
          >
            Previous activity
          </Typography>
          {activities
            .filter(canSeeActivityFilter(person, role))
            .map(
              (activity) => (
                <ActivityItem
                  activity={activity!}
                  key={activity?.id}
                />
              ),
            )}
        </>
      )}

      <Typography
        sx={styles.subhead}
        component="h2"
      >
        Comments
      </Typography>
      {comments.filter(canSeeCommentFilter(person, role)).length > 0
        ? comments
          .filter(canSeeCommentFilter(person, role))
          .map((c) => <CommentBubble key={c.id} message={c} />)
        : (
          <Box sx={{ mt: 2, mb: 4 }}>
            {!isLoading && <Typography>No comments</Typography>}
          </Box>
        )}

      <CommentInput
        isSubmitting={isSubmitting}
        handleComment={handleComment}
        comment={comment}
        setComment={setComment}
      />
    </Box>
  );
};

const PreparedFormSidebar: React.FC<Props> = ({
  className, preparedForm, preparedFormName,
}: Props) => {
  const theme = useTheme();
  const styles = getPreparedFormSidebarStyles(theme);
  const [userState] = useContext(UserContext);
  const { person } = userState;
  const [thisCase] = useContext(CaseContext);

  const isAssignedToSelf = preparedForm.assignedToID === person.id;
  const isCaseManager = thisCase?.caseManagerID === person.id;
  const isComplete = Boolean(preparedForm.finalCaseManagerApproverID);

  const tabs = [
    ...(isAssignedToSelf || (isCaseManager && !isComplete) ? [{
      title: 'Form Details',
      component: () => (
        <DetailsTab
          preparedForm={preparedForm}
          preparedFormName={preparedFormName}
        />
      ),
    }] : []),
    {
      title: 'Form Activity',
      component: () => (
        <ActivitiesTab
          preparedForm={preparedForm}
        />
      ),
    },
  ];

  return (
    <Box sx={styles.root}>
      <Tabs
        label="Form navigation"
        tabs={tabs}
      />
    </Box>
  );
};

export default PreparedFormSidebar;
